import ServiceFamily from '@/model/ServiceFamily/ServiceFamily'
import hasTravellersEligible, {
    IHasTravellersEligible,
} from '@/model/mixins/Traveller/hasTravellersEligible'
import hasTravellersSelected, {
    IHasTravellersSelected,
} from '@/model/mixins/Traveller/hasTravellersSelected'
import Traveller, { ITraveller } from '@/model/Traveller'
import ParticipantService, {
    IParticipantService,
} from '@/model/Service/ParticipantService'
import getIsSansOption from '@/logic/getIsSansOption'
import getQuantitySelectedForOptionFamily from '@/logic/getQuantitySelectedForOptionFamily'
import getSelectedQuantityForOption from '@/logic/getSelectedQuantityForOption'
import { IInsurance } from '../Insurance/Insurance'
import ParticipantInsurance from '../Insurance/ParticipantInsurance'
import { IService } from '../Service/Service'

class ParticipantServiceFamilyBase extends ServiceFamily {
    constructor(id: string) {
        super(id)
    }

    public getEligibleServices(traveller: ITraveller): IParticipantService[] {
        const services: IParticipantService[] = (this.getServices() as IParticipantService[]).filter(
            (service: IParticipantService) => {
                // had to put "any" because the type issues here were too annoying and unclear how to make Typescript happy; I check for types below, so no big problem with "any"
                if (
                    service instanceof ParticipantService &&
                    traveller instanceof Traveller
                ) {
                    const eligibleTravellersIds = (
                        service as IParticipantService
                    )
                        .getTravellersEligible()
                        .map((_) => _.getId())
                    if (eligibleTravellersIds.includes(traveller.getId()))
                        return true
                    return false
                } else {
                    throw new Error(
                        ' you made some mistake here with types, probably'
                    )
                }
            }
        )

        return services
        .sort((a, b) => {
            // Sort by price (from cheapest to most expensive)
            const priceA = a.getTotalPriceByTravellerOrAccomodationId(traveller.getId())
            const priceB = b.getTotalPriceByTravellerOrAccomodationId(traveller.getId())
            return priceA - priceB
        })
        .sort((a, b) => {
            // Service included in the package comes first
            const includedA = a.getTravellersIncluded().map(t => t.getId()).includes(traveller.getId())
            const includedB = b.getTravellersIncluded().map(t => t.getId()).includes(traveller.getId())
            return includedA === includedB ? 0 : includedA ? -1 : 1
        })    
    }

    public getEligibleTravellers(): ITraveller[] {
        const eligibleTravellersSet: Set<ITraveller> = new Set()
        const services: IParticipantService[] =
            this.getServices() as IParticipantService[]
        services.forEach((service: IParticipantService) => {
            // had to put "any" because the type issues here were too annoying and unclear how to make Typescript happy; I check for types below, so no big problem with "any"
            const eligibleTravellers = (
                service as IParticipantService
            ).getTravellersEligible()
            eligibleTravellers.forEach((traveller: ITraveller) => {
                eligibleTravellersSet.add(traveller)
            })
        })
        return Array.from(eligibleTravellersSet)
    }

    public getQuantityNotSelected() {
        const quantitySelected = this.getQuantitySelected()
        const totalTravellersEligible = (
            this as unknown as IHasTravellersEligible
        ).getTravellersEligible().length
        const numberNotSelected = totalTravellersEligible - quantitySelected
        return numberNotSelected
    }

    public getQuantitySelected() {
        let quantitySelected = 0
        let allItems: IService[] = []
        const allServices = this.getServices().filter((item) => {
            // filter out sans services
            if (getIsSansOption(item)) return false
            return true
        })
        allItems = allServices

        allItems.forEach((item: IService | IInsurance) => {
            if (
                item instanceof ParticipantService ||
                item instanceof ParticipantInsurance
            ) {
                quantitySelected += Number(getSelectedQuantityForOption(item))
            }
        })
        return quantitySelected
    }
}
export interface IParticipantServiceFamily
    extends ParticipantServiceFamilyBase,
        IHasTravellersEligible,
        IHasTravellersSelected {
    getEligibleServices(traveller: ITraveller): IParticipantService[]
    getEligibleTravellers(): ITraveller[]
}

// apply mixins
const ParticipantServiceFamily: Constructor<IParticipantServiceFamily> =
    hasTravellersEligible(hasTravellersSelected(ParticipantServiceFamilyBase))

export default ParticipantServiceFamily
